// RUTA: src/components/fitness/CalorieTrackingSection.tsx
//
// Sección de Calorías rediseñada (Carbon / MacroFactor style):
//
//   - Anillo de progreso (consumido / meta) reemplaza las 3 cards
//     "Consumidas / Quemadas / Restantes" — la fórmula de "Restantes"
//     era confusa porque sumaba kcal de cardio (eat-back), patrón
//     abandonado en 2024-26 por las apps serias de coaches.
//   - SIN eat-back: la meta calórica es FIJA. Las kcal de cardio
//     viven en su propia sección.
//   - Macros con barras mini de adherencia.
//   - Streak de logueo (motiva el HÁBITO, no perfección).
//   - Promedio semanal (anti-ansiedad: un día malo no rompe la semana).
//   - Heatmap 12 semanas reemplaza el calendario chiquito + flechas.
//
// Mantiene intactos: HumanFigureIndicator (figura humana iluminándose),
// FoodRegistrationFlow (registro + foto IA), MealSummaryView (desglose
// por comida).

import { useState, useEffect, useMemo } from 'react';
import { Plus, Apple, ArrowLeft } from 'lucide-react';
import { SectionHeader } from './SectionHeader';
import { UserProfile } from '@/types/fitness';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { FoodRegistrationFlow, MealTime } from './FoodRegistrationFlow';
import { MealSummaryView } from './MealSummaryView';
import { CalorieRing, MacroBar } from './CalorieRing';
import { NutritionHistorySection } from './NutritionHistorySection';
import { CoachStreakCard } from './CoachStreakCard';
import { cachedQuery, offlineMutation } from '@/lib/offlineStorage';
import { getTodayLocalString, addDaysLocalString } from '@/lib/dateUtils';
import { celebrateOnce, fireGoalConfetti, todayKey } from '@/lib/celebrate';

interface CalorieTrackingSectionProps {
  perfil: UserProfile;
  activeUser: string;
  viewingUserId?: string | null;
  refreshTrigger?: number;
  initialDate?: string;
}

interface FoodLogRow {
  id: number;
  item_name: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  meal_type: string;
  log_date: string;
}

interface MacroGoals {
  daily_protein: number | null;
  daily_carbs: number | null;
  daily_fats: number | null;
}

export function CalorieTrackingSection({ perfil, activeUser, viewingUserId, refreshTrigger, initialDate }: CalorieTrackingSectionProps) {
  const { user } = useAuth();
  const targetUserId = viewingUserId || user?.id;
  const isCoachView = Boolean(viewingUserId);
  const [selectedDayFoodLogs, setSelectedDayFoodLogs] = useState<FoodLogRow[]>([]);
  const [weekFoodLogs, setWeekFoodLogs] = useState<FoodLogRow[]>([]);
  const [macroGoals, setMacroGoals] = useState<MacroGoals | null>(null);
  const [showFoodFlow, setShowFoodFlow] = useState(false);
  const [initialMealTime, setInitialMealTime] = useState<MealTime | undefined>();
  // Tick local que se incrementa con cada save/delete para forzar
  // refresh del heatmap (NutritionHistorySection lo recibe como
  // refreshKey y re-fetchea sus 12 semanas).
  const [localTick, setLocalTick] = useState(0);

  // El día REAL de hoy (no cambia aunque navegues a un día pasado).
  // Lo usamos para el cálculo de la racha y del promedio 7d.
  const realToday = getTodayLocalString();

  // Día actualmente VISUALIZADO. El cliente puede navegar a un día
  // pasado tocando el heatmap. Si selectedDate === realToday estamos
  // en "modo hoy" y el botón de registrar está visible.
  const [selectedDate, setSelectedDate] = useState<string>(initialDate || realToday);

  // Si el padre cambia initialDate (coach pinchando un heatmap suyo),
  // sincronizamos.
  useEffect(() => {
    if (initialDate) setSelectedDate(initialDate);
  }, [initialDate]);

  const isToday = selectedDate === realToday;

  // El state figureGender se eliminó junto con HumanFigureIndicator.
  // El valor sigue persistido en client_onboarding_profiles por si
  // otro componente lo usa en el futuro (ej. evaluation).

  // Macro goals desde client_goals — cacheado para que offline siga
  // mostrando las metas de proteína/carbos/grasas que el coach puso.
  useEffect(() => {
    if (!targetUserId) return;
    cachedQuery<{ daily_protein: number | null; daily_carbs: number | null; daily_fats: number | null }>(
      `macro_goals_${targetUserId}`,
      () => supabase
        .from('client_goals')
        .select('daily_protein, daily_carbs, daily_fats')
        .eq('client_id', targetUserId)
        .maybeSingle()
    ).then(({ data }) => setMacroGoals(data || null));
  }, [targetUserId]);

  // Comidas del día visualizado (para anillo, macros, MealSummaryView)
  const fetchSelectedDayLogs = async () => {
    if (!targetUserId) return;
    const res = await cachedQuery<any[]>(
      `food_logs_${targetUserId}_${selectedDate}_${selectedDate}`,
      () => {
        let q = supabase
          .from('food_logs')
          .select('*')
          .eq('user_id', targetUserId)
          .eq('log_date', selectedDate)
          .order('created_at', { ascending: true });
        if (activeUser) q = q.eq('profile_name', activeUser);
        return q;
      }
    );
    if (res.data) setSelectedDayFoodLogs(res.data as FoodLogRow[]);
  };

  // Últimos 30 días (para promedio semanal + streak — siempre relativo
  // a HOY, no al día visualizado).
  const fetchWeekLogs = async () => {
    if (!targetUserId) return;
    const start = addDaysLocalString(-29);
    const res = await cachedQuery<any[]>(
      `food_logs_${targetUserId}_${start}_${realToday}`,
      () => {
        let q = supabase
          .from('food_logs')
          .select('id, calories, log_date')
          .eq('user_id', targetUserId)
          .gte('log_date', start)
          .lte('log_date', realToday)
          .order('log_date', { ascending: false });
        if (activeUser) q = q.eq('profile_name', activeUser);
        return q;
      }
    );
    if (res.data) setWeekFoodLogs(res.data as FoodLogRow[]);
  };

  // Refetch del día visualizado cuando cambia
  useEffect(() => {
    fetchSelectedDayLogs();
  }, [targetUserId, activeUser, selectedDate, refreshTrigger]);

  // Week logs son relativos a hoy, refrescan al cambiar usuario o tras log
  useEffect(() => {
    fetchWeekLogs();
  }, [targetUserId, activeUser, refreshTrigger]);

  const handleFoodSaved = (savedItems?: Array<{ item_name: string; calories: number; protein: number; carbs: number; fat: number; meal_type: string; log_date: string }>) => {
    if (savedItems && savedItems.length > 0) {
      const newLogs: FoodLogRow[] = savedItems
        .filter(item => item.log_date === selectedDate)
        .map((item, i) => ({
          id: -(Date.now() + i),
          ...item,
        }));
      setSelectedDayFoodLogs(prev => [...prev, ...newLogs]);

      // Optimistic update sobre weekFoodLogs para que la RACHA y el
      // HEATMAP cambien al instante, sin esperar al roundtrip.
      // Los nuevos logs se inyectan también aquí — luego fetchWeekLogs
      // los reconcilia con la BD (los IDs negativos serán reemplazados
      // por los reales).
      const newLogsForWeek: FoodLogRow[] = savedItems.map((item, i) => ({
        id: -(Date.now() + i + 10000), // offset para evitar colisión
        ...item,
      }));
      setWeekFoodLogs(prev => [...newLogsForWeek, ...prev]);

      // Bump tick para que NutritionHistorySection re-fetchee sus 12
      // semanas y el heatmap pinte la celda nueva al instante.
      setLocalTick(t => t + 1);

      fetchWeekLogs();
    } else {
      fetchSelectedDayLogs();
      fetchWeekLogs();
      setLocalTick(t => t + 1);
    }
  };

  const deleteFood = async (id: number) => {
    await offlineMutation('food_logs', 'delete', { id }, () =>
      supabase.from('food_logs').delete().eq('id', id),
      ['id']
    );
    setSelectedDayFoodLogs(prev => prev.filter(f => f.id !== id));
    setWeekFoodLogs(prev => prev.filter(f => f.id !== id));
    setLocalTick(t => t + 1);
    fetchWeekLogs();
  };

  // ============== Cálculos ==============
  // Totales del día visualizado
  const totalConsumed = selectedDayFoodLogs.reduce((s, f) => s + Number(f.calories), 0);
  const totalProtein  = selectedDayFoodLogs.reduce((s, f) => s + Number(f.protein || 0), 0);
  const totalCarbs    = selectedDayFoodLogs.reduce((s, f) => s + Number(f.carbs || 0), 0);
  const totalFat      = selectedDayFoodLogs.reduce((s, f) => s + Number(f.fat || 0), 0);

  // Streak de logueo: días consecutivos hacia atrás (relativos a HOY,
  // no al día visualizado) con al menos 1 comida.
  //
  // Edge case importante: si HOY todavía no tienes registro pero ayer y
  // antier sí, el streak debería ser 2 (no 0). Solo rompemos al saltar
  // un día completo después de ya haber empezado a contar — el día de
  // hoy en blanco se "pasa" sin romper la racha.
  const streak = useMemo(() => {
    if (weekFoodLogs.length === 0) return 0;
    const distinctDays = new Set(weekFoodLogs.map(l => l.log_date));
    let count = 0;
    const cursor = new Date(realToday + 'T12:00:00');
    let isFirstDay = true;
    let safety = 0;
    while (safety++ < 40) {
      const yyyy = cursor.getFullYear();
      const mm = String(cursor.getMonth() + 1).padStart(2, '0');
      const dd = String(cursor.getDate()).padStart(2, '0');
      const key = `${yyyy}-${mm}-${dd}`;
      if (distinctDays.has(key)) {
        count++;
      } else if (!isFirstDay) {
        break;
      }
      isFirstDay = false;
      cursor.setDate(cursor.getDate() - 1);
    }
    return count;
  }, [weekFoodLogs, realToday]);

  // ¿Hubo registro HOY? (para escoger el copy correcto en CoachStreakCard)
  const loggedToday = useMemo(
    () => weekFoodLogs.some(l => l.log_date === realToday),
    [weekFoodLogs, realToday],
  );

  // Celebración: cuando el cliente cruza la banda verde (85-115% de
  // meta) por primera vez en el DÍA REAL, dispara confetti.
  // Solo se evalúa cuando estás viendo HOY (no al navegar al pasado).
  useEffect(() => {
    if (!isCoachView && isToday && perfil.metaCalorias > 0) {
      const pct = (totalConsumed / perfil.metaCalorias) * 100;
      if (pct >= 85 && pct <= 115) {
        celebrateOnce(`nutrition-goal-${todayKey()}-${targetUserId}`, () => {
          fireGoalConfetti();
        });
      }
    }
  }, [totalConsumed, perfil.metaCalorias, isToday, isCoachView, targetUserId]);

  const openFoodFlow = (mealTime?: MealTime) => {
    setInitialMealTime(mealTime);
    setShowFoodFlow(true);
  };

  if (!user) {
    return (
      <section id="nutrición" className="scroll-mt-32">
        <SectionHeader title="Nutrición & Calorías" subtitle="Balance Energético" icon={Apple} helpId="nutricion" />
        <p className="text-muted-foreground text-center py-12 font-bold uppercase tracking-widest text-sm">
          Inicia sesión para registrar tus calorías
        </p>
      </section>
    );
  }

  // Label corto del día visualizado para el anillo y headers
  const formatShortDay = (dateStr: string) => {
    if (dateStr === realToday) return 'Hoy';
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };
  const formatLongDay = (dateStr: string) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <section id="nutrición" className="scroll-mt-32 space-y-6">
      <SectionHeader title="Nutrición & Calorías" subtitle="Balance Energético" icon={Apple} helpId="nutricion" />

      {/* Banner cuando estás viendo un día pasado */}
      {!isToday && (
        <div className="flex items-center justify-between gap-3 rounded-xl border-2 border-accent/40 bg-accent/5 px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <Apple className="h-4 w-4 text-accent shrink-0" />
            <span className="text-[11px] font-black uppercase tracking-widest text-accent">
              Viendo:
            </span>
            <span className="text-sm font-bold truncate">{formatLongDay(selectedDate)}</span>
          </div>
          <button
            onClick={() => setSelectedDate(realToday)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-accent/10 hover:bg-accent/20 text-accent text-[10px] font-black uppercase tracking-wider transition-colors min-h-[36px] shrink-0"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Volver a hoy
          </button>
        </div>
      )}

      {/* Card de racha firmada por el coach — se subió ARRIBA del aro
       *  por petición del coach. Si no hay coach asignado, cae a un
       *  mensaje genérico sin avatar. */}
      <CoachStreakCard
        streak={streak}
        activity="nutrition"
        clientId={targetUserId}
        clientFirstName={activeUser}
        loggedToday={loggedToday}
      />

      {/* Anillo + status + macros — el hero card que reemplaza las 3 mini-cards.
       *  El HumanFigureIndicator (muñequito) se eliminó porque era redundante
       *  con el aro. La info útil que tenía (badge de estado + mensaje
       *  "Faltan X kcal" / "Te excediste por X kcal") se absorbió justo
       *  debajo del aro con 4 estados: <50% naranja, 50-85% amarillo,
       *  85-110% verde, >110% rojo. */}
      <div className="rounded-xl bg-primary p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Apple className="h-4 w-4 shrink-0 text-accent" />
          <p className="text-[11px] font-black uppercase tracking-widest text-accent">
            {isToday ? 'Calorías de Hoy' : `Calorías del ${formatShortDay(selectedDate)}`}
          </p>
        </div>

        <div className="flex justify-center mb-4">
          <CalorieRing
            consumed={totalConsumed}
            goal={perfil.metaCalorias}
            size={170}
            stroke={14}
            label={formatShortDay(selectedDate)}
          />
        </div>

        <CalorieStatusBadge consumed={totalConsumed} goal={perfil.metaCalorias} />

        {/* Macros con barras mini */}
        <div className="space-y-2.5 pt-3 mt-3 border-t border-primary-foreground/10">
          <MacroBar label="Proteína" current={totalProtein} goal={macroGoals?.daily_protein} />
          <MacroBar label="Carbos"   current={totalCarbs}   goal={macroGoals?.daily_carbs} />
          <MacroBar label="Grasa"    current={totalFat}     goal={macroGoals?.daily_fats} />
        </div>
      </div>

      {/* Botón registrar — solo en HOY (no permitimos registrar en
       *  días pasados desde aquí; eso evita errores frecuentes). */}
      {!isCoachView && isToday && (
        <button onClick={() => openFoodFlow()} className="fitness-btn-primary flex items-center gap-2 justify-center min-h-[48px] w-full">
          <Plus className="w-4 h-4" /> Registrar Alimento
        </button>
      )}

      {/* Comidas del día visualizado */}
      {selectedDayFoodLogs.length > 0 ? (
        <div>
          <h4 className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-3">
            {isToday ? 'Comidas de Hoy' : `Comidas del ${formatShortDay(selectedDate)}`}
          </h4>
          <MealSummaryView
            foodLogs={selectedDayFoodLogs}
            onDelete={isCoachView || !isToday ? undefined : deleteFood}
            onAddToMeal={isCoachView || !isToday ? undefined : (mt) => openFoodFlow(mt)}
          />
        </div>
      ) : (
        <div className="p-8 text-center text-muted-foreground font-bold uppercase tracking-widest text-xs bg-card border-2 border-secondary rounded-xl">
          {isToday
            ? 'Sin registros hoy. ¡Agrega tu primer alimento!'
            : 'Sin comidas registradas este día'}
        </div>
      )}

      {/* Heatmap 12 semanas controlado — al picar un día, todo el
       *  contenido de arriba (anillo, macros, avatar, comidas) refleja
       *  ese día. Si pinchan una celda con el día activo, vuelven a hoy. */}
      {targetUserId && (
        <NutritionHistorySection
          userId={targetUserId}
          profileName={activeUser}
          dailyGoal={perfil.metaCalorias}
          refreshKey={(refreshTrigger || 0) + localTick}
          readOnly={isCoachView}
          selectedDate={selectedDate}
          onSelectDate={(d) => {
            const next = d || realToday;
            setSelectedDate(next);
            // Scroll suave al inicio de la sección si cambió de día,
            // así el cliente ve cómo el anillo / macros / avatar se
            // actualizan con los datos del día seleccionado.
            if (next !== selectedDate) {
              setTimeout(() => {
                document.getElementById('nutrición')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 50);
            }
          }}
        />
      )}

      {/* Modal de registro */}
      <FoodRegistrationFlow
        open={showFoodFlow}
        onClose={() => setShowFoodFlow(false)}
        activeUser={activeUser}
        onSaved={handleFoodSaved}
        initialMealTime={initialMealTime}
      />
    </section>
  );
}

/**
 * Badge + mensaje contextual debajo del aro de calorías.
 *
 * Reemplaza al HumanFigureIndicator (muñequito) que era redundante
 * con el aro. Conserva la info útil que tenía:
 *   - Badge de estado actual
 *   - Mensaje "Faltan X kcal" / "Te excediste por X kcal"
 *
 * 4 estados por porcentaje consumido:
 *   < 50%   → 🟠 Naranja  · "Aún te faltan X kcal — sigue alimentándote"
 *   50–85%  → 🟡 Amarillo · "Faltan X kcal para tu meta"
 *   85–110% → 🟢 Verde    · "¡En meta! Vas excelente"
 *   > 110%  → 🔴 Rojo     · "Te excediste por X kcal"
 */
function CalorieStatusBadge({ consumed, goal }: { consumed: number; goal: number }) {
  if (!goal || goal <= 0) return null;
  const pct = (consumed / goal) * 100;
  const remaining = goal - consumed;
  const excess = consumed - goal;

  let badge = '';
  let message = '';
  let classes = '';

  if (pct < 50) {
    badge = 'Aún muy bajo';
    message = `Aún te faltan ${Math.round(remaining).toLocaleString()} kcal — sigue alimentándote`;
    classes = 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/40';
  } else if (pct < 85) {
    badge = 'En camino';
    message = `Faltan ${Math.round(remaining).toLocaleString()} kcal para tu meta`;
    classes = 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40';
  } else if (pct <= 110) {
    badge = '¡En meta!';
    message = remaining > 0
      ? `Faltan solo ${Math.round(remaining).toLocaleString()} kcal — vas excelente`
      : 'Cumpliste tu meta de hoy — vas excelente';
    classes = 'bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/40';
  } else {
    badge = 'Excedido';
    message = `Te excediste por ${Math.round(excess).toLocaleString()} kcal`;
    classes = 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40';
  }

  return (
    <div className={`rounded-lg border-2 px-3 py-2.5 text-center ${classes}`}>
      <div className="text-[10px] font-black uppercase tracking-widest mb-0.5">{badge}</div>
      <div className="text-sm font-bold">{message}</div>
    </div>
  );
}
